/**
 * ShareFindingSheet — "Para tu agencia" (handoff `Tu tráfico.dc.html`, 4a/4b).
 *
 * Plenty of these stores have someone else running their ads, so the merchant can't
 * act on a finding — they can only ask. This is the ask: a self-contained card and a
 * plain-text payload that survives being pasted into a WhatsApp thread with no
 * context around it.
 *
 * Deliberately lighter than a share feature: the phone's own share sheet or the
 * clipboard. No contacts, no links, no product branding — it's the store's
 * observation, not an invitation to ECLab — and never an instruction, because the
 * person reading it is the one who decides what to do about it.
 */

import { useState } from 'react'
import { Button, Text } from '@nimbus-ds/components'
import type { TrafficCampaignRow } from '@/api/traffic'
import { QUADRANT, campaignMetricLine, isOpaqueCampaignId } from './quadrants'

interface Props {
  row: TrafficCampaignRow
  storeName: string
  periodLabel: string
  onClose: () => void
}

/** The measurement caveat travels WITH the finding — it's useless caveat-free in a thread. */
const FOOTNOTE =
  'Medido en la tienda después del clic; el crédito va a la última fuente que trajo cada visita.'

export function buildShareText(row: TrafficCampaignRow, storeName: string, periodLabel: string): string {
  const meta = QUADRANT[row.quadrant]
  return [
    `Tu tráfico — ${storeName} (${periodLabel})`,
    '',
    `${meta.emoji} ${meta.label}`,
    `Campaña: ${row.campaign}`,
    campaignMetricLine(row),
    meta.line,
    '',
    FOOTNOTE,
  ].join('\n')
}

export function ShareFindingSheet({ row, storeName, periodLabel, onClose }: Props) {
  const [copied, setCopied] = useState(false)
  const meta = QUADRANT[row.quadrant]
  const text = buildShareText(row, storeName, periodLabel)
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  const share = async () => {
    try {
      await navigator.share({ text })
      onClose()
    } catch {
      // Dismissed the OS sheet, or it refused — leave ours open so "Copiar texto" is still there.
    }
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/35"
      role="dialog"
      aria-modal="true"
      aria-label="Para mandarle a quien maneja tus anuncios"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-xl flex-col gap-3.5 rounded-t-xl bg-white p-4 pb-5"
        onClick={(e) => e.stopPropagation()}
      >
        <Text fontWeight="medium">Para mandarle a quien maneja tus anuncios</Text>

        {/* The card the merchant screenshots — same numbers as the row, plus who and when. */}
        <div className="flex flex-col gap-2 rounded-lg border border-[#e7e7e7] bg-[#fafafa] px-4 py-3.5">
          <span className="text-[.6875rem] text-[#888]">
            {storeName} · Tu tráfico · {periodLabel}
          </span>
          <div className="flex items-start gap-2.5">
            <span className="text-xl leading-tight">{meta.emoji}</span>
            <div className="flex min-w-0 flex-col gap-1">
              <Text fontWeight="medium">{meta.label}</Text>
              <span className="text-xs leading-relaxed text-[#5d5d5d] [overflow-wrap:anywhere]">
                {/* Matches the row: an ID gets its noun, so the forwarded card doesn't
                    look like a campaign literally named "120249001851740553". */}
                {isOpaqueCampaignId(row.campaign) ? `Campaña ${row.campaign}` : row.campaign}
              </span>
              <span className="text-[.8125rem] leading-relaxed tabular-nums text-[#0a0a0a]">
                {campaignMetricLine(row)}
              </span>
              <span className="text-xs text-[#5d5d5d]">{meta.line}</span>
            </div>
          </div>
          <span className="border-t border-[#f0f0f0] pt-2 text-[.6875rem] leading-relaxed text-[#888]">
            {FOOTNOTE}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          {canShare && (
            <Button appearance="primary" onClick={share}>
              Compartir…
            </Button>
          )}
          <Button appearance="neutral" onClick={copy}>
            {copied ? 'Texto copiado' : 'Copiar texto'}
          </Button>
          <button type="button" onClick={onClose} className="py-1 text-[.8125rem] text-[#5d5d5d]">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}
